/**
 * SANAL HESAP FORMU — metin alanlarından kural girdisine (back-test,
 * 14.10.2026). Hesap yok; yalnızca biçim çevirisi ve eksik alan denetimi.
 * Asıl doğrulama Worker'da (`sanalTanimDogrula`); burada kullanıcıya erken
 * ve alan başına söylemek için.
 */

import type { Decimal } from "@parafon/contracts";
import {
  HESAP_RENKLERI,
  SANAL_MAX_KAYMA_GUN,
  SANAL_MAX_PERIYOT_ADIM,
  SANAL_MAX_VARLIK,
  type HesapRengi,
  type SanalDengeleme,
  type SanalHesapGirdisi,
  type SanalHesapTanimi,
  type SanalPeriyotBirimi,
  type SanalTemettuKullanimi,
} from "@parafon/contracts/local-api";
import { decimalCompare, parseDecimalInput } from "./decimal";
import { YUZ, kesirMetni, toplam, yuzdeOku, yuzdeYaz } from "./sepetAgirlik";
import { benzersizAd } from "./sanalPaylasim";
import { M } from "../dil";

export interface FormVarlik {
  readonly instrumentId: string;
  readonly ad: string;
  readonly oran: string;
  /** Varsayılan (`kesirliVarsayilan`) ve kullanıcının seçimi. */
  readonly kesirliVarsayilan: boolean;
  readonly kesirli: boolean;
  readonly kilitli: boolean;
}

export interface SanalForm {
  readonly id: string | null;
  readonly tohum: number | null;
  readonly ad: string;
  readonly renk: HesapRengi | "";
  readonly paraBirimi: string;
  readonly varliklar: readonly FormVarlik[];
  readonly baslangic: string;
  readonly bitis: string;
  readonly akis: "duzenli" | "defter";
  readonly periyotAdim: string;
  readonly periyotBirim: SanalPeriyotBirimi;
  readonly baslangicTutari: string;
  readonly periyotTutari: string;
  readonly cekimAcik: boolean;
  readonly cekimTutari: string;
  readonly cekimBaslangic: string;
  readonly defterHesaplari: readonly string[];
  readonly komisyon: string;
  readonly komisyonSabit: string;
  readonly komisyonEnAz: string;
  readonly komisyonEnCok: string;
  /** "" = hesabın para birimi. */
  readonly komisyonParaBirimi: string;
  readonly temettuKomisyonu: boolean;
  readonly artanSonrakiAlima: boolean;
  readonly temettu: SanalTemettuKullanimi;
  readonly dengeleme: SanalDengeleme;
  readonly esik: string;
  readonly kaymaGeri: string;
  readonly kaymaIleri: string;
}

export function bosForm(bugun: string): SanalForm {
  return {
    id: null,
    tohum: null,
    ad: "",
    renk: "",
    paraBirimi: "TRY",
    varliklar: [],
    baslangic: `${Number(bugun.slice(0, 4)) - 3}${bugun.slice(4)}`,
    bitis: "",
    akis: "duzenli",
    periyotAdim: "1",
    periyotBirim: "ay",
    baslangicTutari: "",
    periyotTutari: "",
    cekimAcik: false,
    cekimTutari: "",
    cekimBaslangic: "",
    defterHesaplari: [],
    komisyon: "0",
    komisyonSabit: "",
    komisyonEnAz: "",
    komisyonEnCok: "",
    komisyonParaBirimi: "",
    temettuKomisyonu: true,
    artanSonrakiAlima: true,
    temettu: "ayniVarlik",
    dengeleme: "yok",
    esik: "3",
    kaymaGeri: "0",
    kaymaIleri: "0",
  };
}

/** "0.002" → "0,2" (yüzde). Sayı aritmetiği yok: ondalık noktası kaydırılır. */
export function kesirdenYuzde(k: Decimal): string {
  const neg = k.startsWith("-");
  const g = neg ? k.slice(1) : k;
  const [tam = "0", kes = ""] = g.split(".");
  const birlesik = `${tam}${kes.padEnd(2, "0")}`;
  const t = birlesik.slice(0, tam.length + 2).replace(/^0+(?=\d)/, "");
  const k2 = birlesik.slice(tam.length + 2).replace(/0+$/, "");
  return `${neg ? "-" : ""}${t}${k2 === "" ? "" : `,${k2}`}`;
}

/** "0,2" (yüzde) → "0.002" (kesir). Geçersizse null. */
export function yuzdedenKesir(metin: string): Decimal | null {
  const r = parseDecimalInput(metin);
  if (!r.ok) return null;
  const neg = r.value.startsWith("-");
  const g = neg ? r.value.slice(1) : r.value;
  const [tam = "0", kes = ""] = g.split(".");
  const dolgulu = tam.padStart(3, "0");
  const t = dolgulu.slice(0, -2).replace(/^0+(?=\d)/, "");
  const k = `${dolgulu.slice(-2)}${kes}`.replace(/0+$/, "");
  const sonuc = k === "" ? t : `${t}.${k}`;
  return neg ? `-${sonuc}` : sonuc;
}

export function tanimdanForm(t: SanalHesapTanimi, kesirliVarsayilanOf: (id: string) => boolean, adOf: (id: string) => string): SanalForm {
  const pa = t.paraAkisi;
  return {
    id: t.id,
    tohum: t.tohum,
    ad: t.ad,
    renk: t.renk ?? "",
    paraBirimi: t.paraBirimi,
    varliklar: t.varliklar.map((v) => {
      const vars = kesirliVarsayilanOf(v.instrumentId);
      const oran = yuzdeOku(kesirdenYuzde(v.oran)) ?? 0;
      return {
        instrumentId: v.instrumentId,
        ad: adOf(v.instrumentId),
        oran: yuzdeYaz(oran),
        kesirliVarsayilan: vars,
        kesirli: v.kesirli ?? vars,
        kilitli: v.kilitli === true,
      };
    }),
    baslangic: t.baslangic,
    bitis: t.bitis ?? "",
    akis: pa.tur,
    periyotAdim: pa.tur === "duzenli" ? String(pa.periyot.adim) : "1",
    periyotBirim: pa.tur === "duzenli" ? pa.periyot.birim : "ay",
    baslangicTutari: pa.tur === "duzenli" ? (pa.baslangicTutari ?? "") : "",
    periyotTutari: pa.tur === "duzenli" ? pa.periyotTutari : "",
    cekimAcik: pa.tur === "duzenli" && pa.cekim != null,
    cekimTutari: pa.tur === "duzenli" ? (pa.cekim?.tutar ?? "") : "",
    cekimBaslangic: pa.tur === "duzenli" ? (pa.cekim?.baslangic ?? "") : "",
    defterHesaplari: pa.tur === "defter" ? pa.accountIds : [],
    komisyon: kesirdenYuzde(t.komisyonOrani),
    komisyonSabit: t.komisyonSabit ?? "",
    komisyonEnAz: t.komisyonEnAz ?? "",
    komisyonEnCok: t.komisyonEnCok ?? "",
    komisyonParaBirimi: t.komisyonParaBirimi ?? "",
    temettuKomisyonu: t.temettuKomisyonu !== false,
    artanSonrakiAlima: t.artanSonrakiAlima !== false,
    temettu: t.temettu,
    dengeleme: t.dengeleme,
    esik: t.dengelemeEsigi == null ? "3" : kesirdenYuzde(t.dengelemeEsigi),
    kaymaGeri: String(t.kayma.geri),
    kaymaIleri: String(t.kayma.ileri),
  };
}

// ---------------------------------------------------------------------------
// Kopyala / Farklı kaydet (kullanıcı kararı 01.11.2026)
// ---------------------------------------------------------------------------

type AdRenk = { readonly ad: string; readonly renk?: HesapRengi | undefined };

/** Kapalı listeden (`HESAP_RENKLERI`) henüz hiçbir sanal hesapta olmayan ilk renk; hepsi doluysa renksiz. */
export function ilkBosRenk(mevcut: readonly AdRenk[]): HesapRengi | "" {
  const dolu = new Set(mevcut.map((m) => m.renk));
  return HESAP_RENKLERI.find((r) => !dolu.has(r)) ?? "";
}

/**
 * "Kopyala": kuralın tamamı TOHUMUYLA (fark yalnızca değiştirilen
 * parametreden gelsin), kimliksiz; ad numaralanır, renk kullanılmayan ilk
 * renk. Kaydedilmez, form olarak açılır.
 */
export function kopyaFormu(f: SanalForm, mevcut: readonly AdRenk[]): SanalForm {
  return { ...f, id: null, ad: benzersizAd(f.ad, mevcut.map((m) => m.ad)), renk: ilkBosRenk(mevcut) };
}

/**
 * "Farklı kaydet": düzenlenen hâl yeni sanal hesap olur, asıl kural değişmez.
 * Ad başka bir hesabınkiyle aynıysa numaralanır; renk asılınkiyle aynı
 * kaldıysa kullanılmayan ilk renk.
 */
export function farkliKaydetGirdisi(g: SanalHesapGirdisi, asil: AdRenk | null, mevcut: readonly AdRenk[]): SanalHesapGirdisi {
  const { id: _asil, renk, ...geri } = g;
  const yeniRenk = asil !== null && renk === asil.renk ? ilkBosRenk(mevcut) : (renk ?? "");
  return { ...geri, ad: benzersizAd(g.ad, mevcut.map((m) => m.ad)), ...(yeniRenk === "" ? {} : { renk: yeniRenk }) };
}

export type FormSonucu =
  | { readonly ok: true; readonly girdi: SanalHesapGirdisi }
  | { readonly ok: false; readonly hatalar: Readonly<Record<string, string>> };

function tamSayi(m: string, alt: number, ust: number): number | null {
  if (!/^\d+$/.test(m.trim())) return null;
  const v = Number(m.trim());
  return v >= alt && v <= ust ? v : null;
}

function tutar(m: string): Decimal | null {
  const r = parseDecimalInput(m);
  return r.ok && !r.value.startsWith("-") ? r.value : null;
}

export function formuDonustur(f: SanalForm): FormSonucu {
  const h: Record<string, string> = {};
  if (f.ad.trim() === "") h["ad"] = M.sanal.hata.ad;
  if (f.varliklar.length === 0) h["varliklar"] = M.sanal.hata.enAzBirVarlik;
  if (f.varliklar.length > SANAL_MAX_VARLIK) h["varliklar"] = M.sanal.hata.enCokVarlik(SANAL_MAX_VARLIK);
  const t = toplam(f.varliklar.map((v) => v.oran));
  if (f.varliklar.length > 0) {
    if (t === null) h["varliklar"] = M.sanal.hata.oranBicimi;
    else if (t !== YUZ) h["varliklar"] = M.sanal.hata.oranToplami(yuzdeYaz(t));
    else if (f.varliklar.some((v) => (yuzdeOku(v.oran) ?? 0) === 0)) h["varliklar"] = M.sanal.hata.oranSifir;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.baslangic)) h["baslangic"] = M.sanal.hata.baslangic;
  if (f.bitis !== "" && f.bitis < f.baslangic) h["bitis"] = M.sanal.hata.bitis;

  let paraAkisi: SanalHesapGirdisi["paraAkisi"] | null = null;
  if (f.akis === "duzenli") {
    const adim = tamSayi(f.periyotAdim, 1, SANAL_MAX_PERIYOT_ADIM);
    if (adim === null) h["periyot"] = M.sanal.hata.periyot(SANAL_MAX_PERIYOT_ADIM);
    const pt = f.periyotTutari.trim() === "" ? "0" : tutar(f.periyotTutari);
    if (pt === null) h["periyotTutari"] = M.sanal.hata.tutar;
    const bt = f.baslangicTutari.trim() === "" ? null : tutar(f.baslangicTutari);
    if (f.baslangicTutari.trim() !== "" && bt === null) h["baslangicTutari"] = M.sanal.hata.tutar;
    if (pt === "0" && (bt === null || /^0(\.0*)?$/.test(bt))) {
      h["periyotTutari"] ??= M.sanal.hata.tutarYok;
    }
    let cekim: { tutar: Decimal; baslangic: string } | null = null;
    if (f.cekimAcik) {
      const ct = tutar(f.cekimTutari);
      if (ct === null || /^0(\.0*)?$/.test(ct)) h["cekim"] = M.sanal.hata.cekimSifir;
      else if (!/^\d{4}-\d{2}-\d{2}$/.test(f.cekimBaslangic)) h["cekim"] = M.sanal.hata.cekimBaslangic;
      else if (f.cekimBaslangic < f.baslangic) h["cekim"] = M.sanal.hata.cekimOnce;
      else cekim = { tutar: ct, baslangic: f.cekimBaslangic };
    }
    if (adim !== null && pt !== null) {
      paraAkisi = {
        tur: "duzenli",
        periyot: { birim: f.periyotBirim, adim },
        ...(bt === null ? {} : { baslangicTutari: bt }),
        periyotTutari: pt,
        ...(cekim === null ? {} : { cekim }),
      };
    }
  } else {
    if (f.defterHesaplari.length === 0) h["defter"] = M.sanal.hata.gercekHesap;
    paraAkisi = { tur: "defter", accountIds: f.defterHesaplari };
  }

  const komisyon = f.komisyon.trim() === "" ? "0" : yuzdedenKesir(f.komisyon);
  if (komisyon === null || komisyon.startsWith("-")) h["komisyon"] = M.sanal.hata.komisyon;
  const ucret = (m: string, alan: string): Decimal | null => {
    if (m.trim() === "") return null;
    const v = tutar(m);
    if (v === null) h[alan] = M.sanal.hata.tutar;
    return v;
  };
  const kSabit = ucret(f.komisyonSabit, "komisyonSabit");
  const kEnAz = ucret(f.komisyonEnAz, "komisyonEnAz");
  const kEnCok = ucret(f.komisyonEnCok, "komisyonEnCok");
  if (kEnAz !== null && kEnCok !== null && decimalCompare(kEnCok, kEnAz) < 0) {
    h["komisyonEnCok"] = M.sanal.hata.enCokEnAz;
  }
  let esik: Decimal | null = null;
  if (f.dengeleme === "satarak") {
    esik = yuzdedenKesir(f.esik);
    if (esik === null || esik.startsWith("-") || /^0(\.0*)?$/.test(esik)) h["esik"] = M.sanal.hata.esik;
  }
  const geri = tamSayi(f.kaymaGeri, 0, SANAL_MAX_KAYMA_GUN);
  const ileri = tamSayi(f.kaymaIleri, 0, SANAL_MAX_KAYMA_GUN);
  if (geri === null || ileri === null) h["kayma"] = M.sanal.hata.kayma(SANAL_MAX_KAYMA_GUN);

  if (Object.keys(h).length > 0 || paraAkisi === null) return { ok: false, hatalar: h };
  return {
    ok: true,
    girdi: {
      ...(f.id === null ? {} : { id: f.id }),
      ...(f.tohum === null ? {} : { tohum: f.tohum }),
      ad: f.ad.trim(),
      ...(f.renk === "" ? {} : { renk: f.renk }),
      paraBirimi: f.paraBirimi,
      varliklar: f.varliklar.map((v) => ({
        instrumentId: v.instrumentId,
        oran: kesirMetni(yuzdeOku(v.oran) ?? 0),
        // Yalnızca varsayılandan farklı küsurat seçimi kaydedilir (kullanıcı kararı).
        ...(v.kesirli !== v.kesirliVarsayilan ? { kesirli: v.kesirli } : {}),
        ...(f.dengeleme === "satarak" && v.kilitli ? { kilitli: true } : {}),
      })),
      baslangic: f.baslangic,
      ...(f.bitis === "" ? {} : { bitis: f.bitis }),
      paraAkisi,
      komisyonOrani: komisyon as Decimal,
      ...(kSabit === null ? {} : { komisyonSabit: kSabit }),
      ...(kEnAz === null ? {} : { komisyonEnAz: kEnAz }),
      ...(kEnCok === null ? {} : { komisyonEnCok: kEnCok }),
      ...(f.komisyonParaBirimi === "" || (kSabit === null && kEnAz === null && kEnCok === null)
        ? {}
        : { komisyonParaBirimi: f.komisyonParaBirimi }),
      // Varsayılan "evet": yalnızca "hayır" kaydedilir.
      ...(f.temettuKomisyonu ? {} : { temettuKomisyonu: false }),
      ...(f.artanSonrakiAlima ? {} : { artanSonrakiAlima: false }),
      temettu: f.temettu,
      dengeleme: f.dengeleme,
      ...(esik === null ? {} : { dengelemeEsigi: esik }),
      kayma: { geri: geri as number, ileri: ileri as number },
    },
  };
}
